import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { Posting } from '../services/apiService';
import { AccountAutocomplete } from './ui/AccountAutocomplete';

interface CambioFormProps {
  accounts: string[];
  onSubmit: (data: {
    date: string;
    payee: string;
    postings: Posting[];
    tags?: Record<string, string>;
  }) => Promise<void>;
  onCancel: () => void;
}

const PLATFORMS = ['Wise', 'BS2', 'Remessa Online', 'PayPal', 'Outro'];
const IOF_RATE = 0.0038; // 0.38% PJ

export const CambioForm: React.FC<CambioFormProps> = ({ accounts, onSubmit, onCancel }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [cliente, setCliente] = useState('');
  const [contrato, setContrato] = useState('');
  const [valorUSD, setValorUSD] = useState(0);
  const [taxaCambio, setTaxaCambio] = useState(0);
  const [plataforma, setPlataforma] = useState('Wise');
  const [iofManual, setIofManual] = useState<number | null>(null);
  const [valorBRLRecebido, setValorBRLRecebido] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Editable account names with defaults
  const [contaBanco, setContaBanco] = useState('Ativos:Banco');
  const [contaCambio, setContaCambio] = useState('Despesas:Câmbio');
  const [contaIOF, setContaIOF] = useState('Despesas:Impostos:IOF');
  const [contaReceita, setContaReceita] = useState('Receitas:Serviços');

  const valorBRLBruto = valorUSD * taxaCambio;
  const iof = iofManual !== null ? iofManual : Number((valorBRLBruto * IOF_RATE).toFixed(2));
  const despesaCambio = Number((valorBRLBruto - valorBRLRecebido - iof).toFixed(2));
  const valorReceita = Number((valorBRLRecebido + despesaCambio + iof).toFixed(2));

  const previewLines = [
    `${date.replace(/-/g, '/')} Recebimento ${cliente || 'Cliente'} (${plataforma})`,
    `    ; cliente: ${cliente}`,
    contrato ? `    ; contrato: ${contrato}` : null,
    `    ; taxa-cambio: ${taxaCambio}`,
    `    ; plataforma: ${plataforma}`,
    `    ${contaBanco}${' '.repeat(Math.max(1, 36 - contaBanco.length))}BRL ${valorBRLRecebido.toFixed(2)}`,
    despesaCambio > 0
      ? `    ${contaCambio}${' '.repeat(Math.max(1, 36 - contaCambio.length))}BRL ${despesaCambio.toFixed(2)}`
      : null,
    iof > 0
      ? `    ${contaIOF}${' '.repeat(Math.max(1, 36 - contaIOF.length))}BRL ${iof.toFixed(2)}`
      : null,
    `    ${contaReceita}${' '.repeat(Math.max(1, 36 - contaReceita.length))}BRL -${valorReceita.toFixed(2)}`,
  ]
    .filter(Boolean)
    .join('\n');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const postings: Posting[] = [
        { account: contaBanco, amount: valorBRLRecebido, commodity: 'BRL' },
      ];
      if (despesaCambio > 0) {
        postings.push({ account: contaCambio, amount: despesaCambio, commodity: 'BRL' });
      }
      if (iof > 0) {
        postings.push({ account: contaIOF, amount: iof, commodity: 'BRL' });
      }
      postings.push({ account: contaReceita, amount: -valorReceita, commodity: 'BRL' });

      const tags: Record<string, string> = {
        cliente,
        'taxa-cambio': String(taxaCambio),
        plataforma,
      };
      if (contrato) tags.contrato = contrato;

      await onSubmit({
        date: date.replace(/-/g, '/'),
        payee: `Recebimento ${cliente} (${plataforma})`,
        postings,
        tags,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data da operação</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Plataforma</label>
          <select
            value={plataforma}
            onChange={(e) => setPlataforma(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
          <input
            type="text"
            value={cliente}
            onChange={(e) => setCliente(e.target.value)}
            placeholder="Nome do cliente"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contrato (opcional)</label>
          <input
            type="text"
            value={contrato}
            onChange={(e) => setContrato(e.target.value)}
            placeholder="Ex: ABC-123"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Valor USD bruto</label>
          <input
            type="number"
            step="0.01"
            value={valorUSD || ''}
            onChange={(e) => setValorUSD(parseFloat(e.target.value) || 0)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Taxa de câmbio</label>
          <input
            type="number"
            step="0.0001"
            value={taxaCambio || ''}
            onChange={(e) => setTaxaCambio(parseFloat(e.target.value) || 0)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Valor BRL recebido</label>
          <input
            type="number"
            step="0.01"
            value={valorBRLRecebido || ''}
            onChange={(e) => setValorBRLRecebido(parseFloat(e.target.value) || 0)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Contas</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Conta banco</label>
            <AccountAutocomplete
              value={contaBanco}
              accounts={accounts}
              onChange={setContaBanco}
              className="w-full border rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Conta receita</label>
            <AccountAutocomplete
              value={contaReceita}
              accounts={accounts}
              onChange={setContaReceita}
              className="w-full border rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Conta despesa câmbio</label>
            <AccountAutocomplete
              value={contaCambio}
              accounts={accounts}
              onChange={setContaCambio}
              className="w-full border rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Conta IOF</label>
            <AccountAutocomplete
              value={contaIOF}
              accounts={accounts}
              onChange={setContaIOF}
              className="w-full border rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <h4 className="text-sm font-medium text-gray-700">Cálculos automáticos</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-gray-500">BRL bruto:</span>
            <span className="ml-1 font-medium">R$ {valorBRLBruto.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-500">IOF (0,38%):</span>
            <input
              type="number"
              step="0.01"
              value={iof}
              onChange={(e) => setIofManual(parseFloat(e.target.value) || 0)}
              className="ml-1 w-24 border rounded px-2 py-0.5 text-sm"
            />
          </div>
          <div>
            <span className="text-gray-500">Despesa câmbio:</span>
            <span className={`ml-1 font-medium ${despesaCambio > 0 ? 'text-red-600' : 'text-green-600'}`}>
              R$ {despesaCambio.toFixed(2)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Receita total:</span>
            <span className="ml-1 font-medium">R$ {valorReceita.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <Eye className="h-4 w-4" />
          {showPreview ? 'Ocultar preview' : 'Preview ledger'}
        </button>
        {showPreview && (
          <pre className="bg-gray-900 text-green-400 text-sm p-4 rounded-lg overflow-x-auto font-mono">
            {previewLines}
          </pre>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Salvando...' : 'Lançar Câmbio'}
        </button>
      </div>
    </form>
  );
};
